/**
 * Drop-acceptance rules for routing 3D assets into the Pose 3D editor.
 *
 * These live outside the component for the same reason pose3DBackground.ts does:
 * ImageUpload is a .vue single-file component and the unit runner has no vue
 * transform, so predicates left inline could only be asserted against as raw
 * source text.
 */

import { ASSET_3D_SUPPORTED_EXTENSIONS } from './asset3DFormats';

export const ASSET_3D_UPLOAD_TYPE = 'ASSET_3D';

/**
 * The MIME types browsers actually report for the formats we accept. glTF text
 * and OBJ/STL frequently arrive as '' or a generic octet-stream instead, which
 * is why isPossibleAsset3DDragItem cannot rely on this list alone.
 */
const ASSET_3D_MIME_TYPES: string[] = [
	'model/gltf-binary',
	'model/gltf+json',
	'model/obj',
	'model/stl',
	'application/sla',
];

const GENERIC_BINARY_MIME_TYPES: string[] = [
	'',
	'application/octet-stream',
];

export interface DraggedItemLike {
	type?: string | null;
	nodeRunResult?: { type?: string | null } | null;
	upload?: { type?: string | null } | null;
	avatar?: unknown;
}

export interface DataTransferItemLike {
	kind?: string;
	type?: string;
}

export function isAsset3DFileName (name: string | null | undefined): boolean {
	const ext: string = (name || '').split('.').pop()?.toLowerCase() || '';
	return !!ext && ASSET_3D_SUPPORTED_EXTENSIONS.includes(ext);
}

/**
 * Whether a dragged library item carries a 3D asset rather than an image. The
 * dragAndDrop store labels every gallery drag 'RUNNIT_IMAGE' regardless of what
 * was picked up, so the real discriminator is the payload's own type.
 */
export function isAsset3DDraggedItem (draggedItem: DraggedItemLike | null | undefined): boolean {
	if (!draggedItem) return false;
	const resultType = draggedItem.nodeRunResult?.type || null;
	const uploadType = draggedItem.upload?.type || null;
	return resultType === ASSET_3D_UPLOAD_TYPE || uploadType === ASSET_3D_UPLOAD_TYPE;
}

/**
 * Whether an in-flight OS file drag might be a 3D model.
 *
 * dragover exposes only `kind` and `type`, never the filename, so an .obj or
 * .stl reporting '' is indistinguishable from any other extensionless binary.
 * Accepting generic binaries here keeps the drop zone from rejecting valid
 * models mid-drag; handleFiles re-checks the real extension once the drop lands
 * and rejects anything that is not actually a supported model.
 */
export function isPossibleAsset3DDragItem (item: DataTransferItemLike | null | undefined): boolean {
	if (!item || item.kind !== 'file') return false;
	const type: string = (item.type || '').toLowerCase();
	return ASSET_3D_MIME_TYPES.includes(type) || GENERIC_BINARY_MIME_TYPES.includes(type);
}

export function dataTransferHasPossibleAsset3D (items: ArrayLike<DataTransferItemLike> | null | undefined): boolean {
	if (!items) return false;
	for (let i = 0; i < items.length; i++) {
		if (isPossibleAsset3DDragItem(items[i])) return true;
	}
	return false;
}

/**
 * The single decision the drop zone asks on dragover and again on drop.
 *
 * `hasPose3D` gates everything: a field without the 3D affordance must keep
 * rejecting 3D payloads exactly as it did before, so a stray .glb never lands
 * in an ordinary image input.
 */
export function acceptsAsset3DDrop (
	hasPose3D: boolean,
	draggedItem: DraggedItemLike | null | undefined,
	items: ArrayLike<DataTransferItemLike> | null | undefined,
): boolean {
	if (!hasPose3D) return false;
	if (isAsset3DDraggedItem(draggedItem)) return true;
	return dataTransferHasPossibleAsset3D(items);
}
